import { useState, useEffect } from 'react'

const CANVAS_SIZE = 50
const ALPHA_THRESHOLD = 10
const WHITE_LUMINANCE_THRESHOLD = 0.75
const BLACK_LUMINANCE_THRESHOLD = 0.25
const MID_RANGE_BG = '#808080'

function computeBgColor(data: Uint8ClampedArray): string | undefined {
  let totalLuminance = 0
  let opaquePixelCount = 0

  for (let i = 0; i < data.length; i += 4) {
    const alpha = data[i + 3]
    if (alpha < ALPHA_THRESHOLD) continue

    const r = data[i] / 255
    const g = data[i + 1] / 255
    const b = data[i + 2] / 255

    totalLuminance += 0.2126 * r + 0.7152 * g + 0.0722 * b
    opaquePixelCount++
  }

  if (opaquePixelCount === 0) return undefined

  const avgLuminance = totalLuminance / opaquePixelCount

  if (avgLuminance > WHITE_LUMINANCE_THRESHOLD) return '#000000'
  if (avgLuminance < BLACK_LUMINANCE_THRESHOLD) return '#FFFFFF'
  return MID_RANGE_BG
}

export function useLogoBgColor(logoUrl: string | null): string | undefined {
  const [result, setResult] = useState<{
    url: string
    color: string | undefined
  } | null>(null)

  useEffect(() => {
    if (!logoUrl) return

    let cancelled = false
    const img = new window.Image()
    img.crossOrigin = 'anonymous'

    img.onload = () => {
      if (cancelled) return
      try {
        const canvas = document.createElement('canvas')
        canvas.width = CANVAS_SIZE
        canvas.height = CANVAS_SIZE

        const ctx = canvas.getContext('2d')
        if (!ctx) return

        ctx.drawImage(img, 0, 0, CANVAS_SIZE, CANVAS_SIZE)
        const { data } = ctx.getImageData(0, 0, CANVAS_SIZE, CANVAS_SIZE)
        setResult({ url: logoUrl, color: computeBgColor(data) })
      } catch {
        setResult({ url: logoUrl, color: undefined })
      }
    }

    img.onerror = () => {
      if (!cancelled) setResult({ url: logoUrl, color: undefined })
    }

    img.src = logoUrl

    return () => {
      cancelled = true
    }
  }, [logoUrl])

  if (!logoUrl) return undefined
  return result?.url === logoUrl ? result.color : undefined
}
